/**
 * Web twin of apps/mobile/components/TaskEdit/PhotoMapViewer.js (read-only).
 *
 * 3:4 hero with:
 *  - highlight overlay (hide while pressing / holding the photo)
 *  - optional photo ↔ Google Maps toggle when `location` is present
 *  - assignee + supporters on the same bottom overlay row as native Task Edit
 *
 * Overlay sits in the native bottom slot: left/right `spacing.md`,
 * bottom `spacing.xl` (16 / 30), above the sheet tuck.
 */
import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { imageOutline, mapOutline, peopleOutline, personOutline } from 'ionicons/icons';
import { cn } from '@/lib/utils';
import IonIcon from './IonIcon';
import TaskHighlightOverlay, { hasHighlightContent } from './TaskHighlightOverlay';
import TaskMap, { hasMapsKey, useMapsFailed } from '../../home/components/TaskMap';
import TaskMapMarker from '../../home/components/TaskMapMarker';
import type { TaskFollower } from '../../home/useTaskFollowers';
import { googleMapsViewSrc, hasMapLocation, type TaskLocation } from './mapEmbed';

export type { TaskLocation } from './mapEmbed';

export interface OverlayAssignee {
  name: string;
  photoURL: string | null;
}

interface Props {
  photoUrl: string | null;
  hasOverlay?: boolean;
  highlightSvg?: string | null;
  drawingPaths?: unknown;
  location?: TaskLocation | null;
  /** Marker artwork when the map is up — same pin as the bucket map. */
  title?: string;
  status?: string;
  alt?: string;
  /**
   * Overrides the hero's shape. The 3:4 phone hero is taller than a desktop
   * modal can spend on one photo, so the desktop report panel flattens it and
   * lets `object-cover` crop — merged with `cn`, so `aspect-*` / `max-w-*` here
   * replace the defaults rather than fighting them.
   */
  className?: string;
  assignee?: OverlayAssignee | null;
  followers?: TaskFollower[];
  showAssignee?: boolean;
  showFollowers?: boolean;
}

const OVERLAY_BTN =
  'pointer-events-auto flex size-11 shrink-0 items-center justify-center rounded-full border-2 border-white/30 bg-white text-x9-ink shadow-md transition active:scale-95';

export default function PhotoMapViewer({
  photoUrl,
  hasOverlay = false,
  highlightSvg = null,
  drawingPaths = null,
  location = null,
  title,
  status = 'pending',
  alt = 'Task photo',
  className,
  assignee = null,
  followers = [],
  showAssignee = true,
  showFollowers = true,
}: Props) {
  const [showMap, setShowMap] = useState(false);
  const [mapMounted, setMapMounted] = useState(false);
  const [hideOverlay, setHideOverlay] = useState(false);
  const [followersOpen, setFollowersOpen] = useState(false);

  const hasLocation = hasMapLocation(location);
  const showPeople = showAssignee || showFollowers;
  const showChrome = hasLocation || showPeople;

  const showingMap = showMap && hasLocation;
  const showHighlight =
    !showingMap && !hideOverlay && hasHighlightContent(highlightSvg, drawingPaths, hasOverlay);

  const onPressIn = useCallback(() => {
    if (!showingMap) setHideOverlay(true);
  }, [showingMap]);
  const onPressOut = useCallback(() => setHideOverlay(false), []);

  return (
    <div
      className={cn(
        'relative mx-auto aspect-[3/4] w-full max-w-md shrink-0 overflow-hidden bg-black [contain:layout_paint]',
        className,
      )}
    >
      <div
        className="absolute inset-0 select-none"
        onPointerDown={onPressIn}
        onPointerUp={onPressOut}
        onPointerCancel={onPressOut}
        onPointerLeave={onPressOut}
        style={{ touchAction: showingMap ? 'auto' : 'manipulation' }}
      >
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={alt}
            className={cn(
              'absolute inset-0 size-full object-cover',
              showingMap && 'invisible',
            )}
            loading="eager"
            draggable={false}
          />
        ) : (
          <div
            className={cn(
              'absolute inset-0 flex items-center justify-center bg-x9-marine/80 text-sm text-white/50',
              showingMap && 'invisible',
            )}
          >
            No photo
          </div>
        )}

        {hasLocation && mapMounted ? (
          <div
            className={cn(
              'absolute inset-0 overflow-hidden',
              showingMap ? 'z-[1]' : 'invisible pointer-events-none',
            )}
            onWheel={(event) => event.stopPropagation()}
          >
            <TaskLocationMap
              location={location!}
              title={title || alt}
              status={status}
              photoUrl={photoUrl}
            />
          </div>
        ) : null}

        {showHighlight && (
          <TaskHighlightOverlay highlightSvg={highlightSvg} drawingPaths={drawingPaths} />
        )}
      </div>

      {showChrome && (
        <div
          className="pointer-events-none absolute inset-x-4 z-10 flex items-end justify-between"
          style={{ bottom: 30 }}
        >
          <div className="flex items-center gap-2">
            {hasLocation ? (
              <button
                type="button"
                onClick={() => {
                  setShowMap((v) => !v);
                  setMapMounted(true);
                }}
                aria-label={showingMap ? 'Switch to image view' : 'Switch to map view'}
                className={OVERLAY_BTN}
              >
                <IonIcon icon={showingMap ? imageOutline : mapOutline} size={24} />
              </button>
            ) : null}
          </div>

          {showPeople ? (
            <div className="flex items-center gap-2">
              {showAssignee ? <AssigneeButton assignee={assignee} /> : null}
              {showFollowers ? (
                <FollowersButton followers={followers} onOpen={() => setFollowersOpen(true)} />
              ) : null}
            </div>
          ) : null}
        </div>
      )}

      {followersOpen ? (
        <FollowersList followers={followers} onClose={() => setFollowersOpen(false)} />
      ) : null}
    </div>
  );
}

function AssigneeButton({ assignee }: { assignee: OverlayAssignee | null }) {
  return (
    <span
      role="img"
      aria-label={
        assignee
          ? `Assigned to ${assignee.name}`
          : 'Contact assignment (read-only)'
      }
      className={`${OVERLAY_BTN} overflow-hidden border-white/95`}
    >
      {assignee?.photoURL ? (
        <img
          src={assignee.photoURL}
          alt=""
          className="size-[38px] rounded-full object-cover"
          referrerPolicy="no-referrer"
        />
      ) : assignee ? (
        <span className="text-[13px] font-semibold">{initials(assignee.name)}</span>
      ) : (
        <IonIcon icon={personOutline} size={19} />
      )}
    </span>
  );
}

function FollowersButton({
  followers,
  onOpen,
}: {
  followers: TaskFollower[];
  onOpen: () => void;
}) {
  const filled = followers.length > 0;
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label="View task supporters"
      className={cn(
        OVERLAY_BTN,
        filled && 'min-w-0 border-0 bg-transparent px-0 shadow-none',
        filled && 'h-11 w-auto',
      )}
    >
      {filled ? <FollowersFaces followers={followers} /> : <IonIcon icon={peopleOutline} size={19} />}
    </button>
  );
}

function FollowersFaces({ followers }: { followers: TaskFollower[] }) {
  const faces = followers.slice(0, Math.min(3, followers.length));
  return (
    <span className="flex items-center">
      {faces.map((follower, index) => (
        <span
          key={follower.userId}
          className="relative flex size-11 items-center justify-center"
          style={{ marginLeft: index > 0 ? -16 : 0 }}
        >
          <Face
            name={follower.publicDisplayName || '?'}
            photoURL={follower.photoURL}
          />
        </span>
      ))}
      <span
        className="relative flex size-11 items-center justify-center rounded-full border-[3px] border-white bg-[#e8e8ed] text-[13px] font-bold text-x9-ink"
        style={{ marginLeft: faces.length > 0 ? -16 : 0 }}
      >
        {followers.length}
      </span>
    </span>
  );
}

function Face({ name, photoURL }: { name: string; photoURL: string | null }) {
  return (
    <span className="flex size-[38px] items-center justify-center overflow-hidden rounded-full border-[3px] border-white bg-[#e8e8ed] text-[11px] font-semibold text-x9-ink">
      {photoURL ? (
        <img
          src={photoURL}
          alt=""
          className="size-full object-cover"
          referrerPolicy="no-referrer"
        />
      ) : (
        initials(name)
      )}
    </span>
  );
}

function FollowersList({
  followers,
  onClose,
}: {
  followers: TaskFollower[];
  onClose: () => void;
}) {
  const { t } = useTranslation(['tasks', 'common']);
  const title = t('tasks:followers.title', { defaultValue: 'Supporters' });
  const empty = t('tasks:followers.empty', {
    defaultValue: 'No one is following this task yet.',
  });

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-6"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="task-supporters-title"
        className="w-full max-w-[22.5rem] rounded-2xl bg-popover p-5 shadow-lg"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 id="task-supporters-title" className="text-base font-semibold text-foreground">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-medium text-muted-foreground"
          >
            {t('common:actions.close', { defaultValue: 'Close' })}
          </button>
        </div>
        {followers.length === 0 ? (
          <p className="py-3 text-sm text-muted-foreground">{empty}</p>
        ) : (
          <ul className="max-h-80 overflow-y-auto">
            {followers.map((follower) => {
              const name = follower.publicDisplayName?.trim() || '?';
              const since = follower.followedAt
                ? t('tasks:followers.since', {
                    date: new Date(follower.followedAt).toLocaleDateString(),
                    defaultValue: 'Following since {{date}}',
                  })
                : null;
              return (
                <li key={follower.userId} className="flex items-center gap-3 py-2">
                  <Face name={name} photoURL={follower.photoURL} />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">{name}</p>
                    {since ? <p className="text-xs text-muted-foreground">{since}</p> : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>,
    document.body,
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] || '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}

/**
 * One map, one pin. The pin is a CSS overlay so it does not wait on tiles —
 * and so a late JS-API failure cannot swap in a second embed mid-view.
 */
function TaskLocationMap({
  location,
  title,
  status,
  photoUrl,
}: {
  location: TaskLocation;
  title: string;
  status: string;
  photoUrl: string | null;
}) {
  const mapsFailed = useMapsFailed();
  const [useJsMap] = useState(() => hasMapsKey() && !mapsFailed);

  return (
    <div className="absolute inset-0 size-full overflow-hidden">
      {useJsMap ? (
        <TaskMap
          tasks={[
            {
              id: 'here',
              title,
              status,
              photoUrl,
              location: { latitude: location.latitude, longitude: location.longitude },
            },
          ]}
          className="absolute inset-0 size-full"
          showMarkers={false}
          onOpen={() => undefined}
        />
      ) : (
        <iframe
          title="Task location"
          src={googleMapsViewSrc(location.latitude, location.longitude)}
          className="absolute inset-0 size-full border-0"
          referrerPolicy="no-referrer-when-downgrade"
        />
      )}
      <div className="pointer-events-none absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-full">
        <TaskMapMarker photoUrl={photoUrl} status={status} title={title} />
      </div>
    </div>
  );
}
